/**
 * Bestsellers Grid - Swiper Slider
 * Features:
 * - Hover overlay with product details
 * - AJAX add to cart (simple products only)
 * - Judge.me integration
 * - Autoplay with 6s delay
 * - Color swatches as links to product page
 */

class BestsellersGrid {
  constructor(container) {
    this.container = container;
    this.swiper = null;
    this.init();
  }

  init() {
    // Wait for Swiper library to load
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    this.initSwiper();
    this.setupAddToCart();
  }

  /**
   * Initialize Swiper with 2-row grid
   */
  initSwiper() {
    const swiperElement = this.container.querySelector('.bestsellers-swiper');
    if (!swiperElement) return;

    // Get settings from data attributes
    const autoplay = swiperElement.dataset.autoplay === 'true';
    const autoplayDelay = parseInt(swiperElement.dataset.autoplayDelay) || 6000;
    const slidesDesktop = parseFloat(swiperElement.dataset.slidesDesktop) || 3.2;
    const slidesTablet = parseFloat(swiperElement.dataset.slidesTablet) || 2.3;
    const slidesMobile = parseFloat(swiperElement.dataset.slidesMobile) || 1.2;
    const spaceBetween = parseInt(swiperElement.dataset.spaceBetween) || 32;

    // Initialize Swiper - Single row
    this.swiper = new Swiper(swiperElement, {
      // Core settings
      slidesPerView: slidesMobile,
      spaceBetween: 16,
      loop: false,
      speed: 600,
      grabCursor: true,
      watchOverflow: true,

      // Autoplay
      autoplay: autoplay ? {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      } : false,

      // Navigation
      navigation: {
        nextEl: this.container.querySelector('.bestsellers-button--next'),
        prevEl: this.container.querySelector('.bestsellers-button--prev'),
      },

      // Pagination
      pagination: {
        el: this.container.querySelector('.bestsellers-pagination'),
        clickable: true,
        dynamicBullets: true,
        dynamicMainBullets: 3,
      },

      // Responsive breakpoints
      breakpoints: {
        // Mobile (>= 480px)
        480: {
          slidesPerView: slidesMobile,
          spaceBetween: 16,
        },
        // Tablet (>= 750px)
        750: {
          slidesPerView: slidesTablet,
          spaceBetween: 20,
        },
        // Desktop (>= 990px)
        990: {
          slidesPerView: slidesTablet,
          spaceBetween: spaceBetween,
        },
        // Large Desktop (>= 1200px)
        1200: {
          slidesPerView: slidesDesktop,
          spaceBetween: spaceBetween,
        },
      },

      // Accessibility
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        paginationBulletMessage: 'Go to slide {{index}}',
      },

      // Keyboard control
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },

      // Events
      on: {
        init: function() {
          swiperElement.classList.add('swiper-initialized');
        },
        slideChange: function() {
          // Optional: Analytics tracking
          if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push({
              'event': 'bestseller_slide_change',
              'slideIndex': this.activeIndex
            });
          }
        },
      },
    });

    // Pause autoplay when page is hidden
    if (autoplay) {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.swiper.autoplay.stop();
        } else {
          this.swiper.autoplay.start();
        }
      });
    }

    // Pause on hover (additional to pauseOnMouseEnter)
    const cards = this.container.querySelectorAll('.bestseller-card');
    cards.forEach(card => {
      card.addEventListener('mouseenter', () => {
        if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.running) {
          this.swiper.autoplay.stop();
        }
      });

      card.addEventListener('mouseleave', () => {
        if (this.swiper && this.swiper.autoplay && autoplay) {
          this.swiper.autoplay.start();
        }
      });
    });
  }

  /**
   * AJAX Add to Cart (for simple products only)
   */
  setupAddToCart() {
    // Only handle button elements (not links for variant products)
    const addToCartButtons = this.container.querySelectorAll('button.bestseller-add-to-cart');

    addToCartButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent card link click

        const variantId = button.dataset.variantId;
        
        // Disable button during request
        button.disabled = true;
        button.classList.add('loading');

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1
            })
          });

          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }

          const data = await response.json();
          
          // Show success toast
          this.showToast('Dodano do koszyka!');
          
          // Update cart count
          this.updateCartCount();
          
          // Trigger cart refresh event
          if (typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(new CustomEvent('cart:refresh'));
          }

          // Optional: Track add to cart
          if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push({
              'event': 'add_to_cart',
              'ecommerce': {
                'items': [{
                  'item_id': variantId,
                  'quantity': 1
                }]
              }
            });
          }

        } catch (error) {
          console.error('Add to cart error:', error);
          this.showToast('Wystąpił błąd. Spróbuj ponownie.', 'error');
        } finally {
          button.disabled = false;
          button.classList.remove('loading');
        }
      });
    });
  }

  /**
   * Show toast notification
   */
  showToast(message, type = 'success') {
    const toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;

    const textElement = toast.querySelector('.cart-toast-text');
    if (textElement) {
      textElement.textContent = message;
    }

    toast.classList.remove('show', 'error');
    if (type === 'error') {
      toast.classList.add('error');
    }

    // Show toast
    setTimeout(() => {
      toast.classList.add('show');
    }, 10);

    // Hide after 3 seconds
    setTimeout(() => {
      toast.classList.remove('show');
    }, 3000);
  }

  /**
   * Update cart count badge
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count elements
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
        element.classList.add('updated');
        setTimeout(() => {
          element.classList.remove('updated');
        }, 300);
      });
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }

  /**
   * Clean up on disconnect
   */
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.bestsellers-section');
  containers.forEach(container => {
    new BestsellersGrid(container);
  });
});

// Reinitialize on Shopify section reload (Theme Editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.bestsellers-section');
    if (container) {
      new BestsellersGrid(container);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const container = event.target.querySelector('.bestsellers-section');
    if (container && container._bestsellersGrid) {
      container._bestsellersGrid.destroy();
    }
  });
}

// Keyboard accessibility - ESC to close overlays
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    const activeOverlay = document.querySelector('.bestseller-card:hover');
    if (activeOverlay) {
      activeOverlay.blur();
    }
  }
});
